import { apiFetch } from '@/utils/apiFetch';
import { countryFilterToParams } from '@/lib/competitorDisplay';
import { normalizeTenantId } from '@/lib/tenantUtils';

const API_BASE_URL = (() => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'https://api.neogtm.com';
    }
  }
  return 'https://api.neogtm.com';
})();

const MODULE_STATUS_KEY = 'competitor-module-status-v4'; // bump: shape changed, evicts v3
const DISABLED_TTL_MS = 60_000; // a re-probe is 4 cheap GETs; a stuck tab is unrecoverable
const competitorCountriesCache = new Map();

/** Preserve mixed-case V2 ids (feedbackfruits_V2 ≠ feedbackfruits_v2). */
export function competitorTenantId(tenantId) {
  return normalizeTenantId(tenantId) || (tenantId ? String(tenantId).trim() : '');
}

function competitorHeaders(tenantId) {
  const headers = { 'Content-Type': 'application/json' };
  const tid = competitorTenantId(tenantId);
  if (tid) headers['X-Tenant-ID'] = tid;
  return headers;
}

function buildQuery(tenantId, params = {}) {
  const qs = new URLSearchParams();
  const tid = competitorTenantId(tenantId);
  qs.set('tenant_id', tid);
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  });
  return qs.toString();
}

export class CompetitorModuleDisabledError extends Error {
  constructor() {
    super('Competitor module disabled');
    this.name = 'CompetitorModuleDisabledError';
    this.status = 404;
  }
}

async function competitorFetch(path, tenantId, params = {}) {
  const tid = competitorTenantId(tenantId);
  const url = `${API_BASE_URL}${path}?${buildQuery(tid, params)}`;
  const res = await fetch(url, {
    credentials: 'include',
    headers: competitorHeaders(tid),
  });
  if (res.status === 404) {
    throw new CompetitorModuleDisabledError();
  }
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    const err = new Error(text || `Competitor API failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export function getCachedCompetitorModuleStatus(tenantId) {
  try {
    const raw = sessionStorage.getItem(`${MODULE_STATUS_KEY}:${competitorTenantId(tenantId)}`);
    if (!raw) return null;
    const { status, at } = JSON.parse(raw);
    // 'disabled' reflects a SERVER-SIDE ENTITLEMENT that an admin can flip at any
    // time. Caching it for the life of the tab made the Competitors tab
    // unrecoverable without closing the tab - sessionStorage survives reload.
    // Same reasoning that already exempts 'empty' below.
    if (status === 'disabled' && Date.now() - at > DISABLED_TTL_MS) return null;
    return status;
  } catch {
    return null;
  }
}

export function setCachedCompetitorModuleStatus(tenantId, status) {
  try {
    sessionStorage.setItem(
      `${MODULE_STATUS_KEY}:${competitorTenantId(tenantId)}`,
      JSON.stringify({ status, at: Date.now() }),
    );
  } catch {}
}

async function competitorFetchAllowMissing(path, tenantId, params = {}) {
  try {
    return await competitorFetch(path, tenantId, params);
  } catch (e) {
    if (e instanceof CompetitorModuleDisabledError || e?.status === 404) {
      return { rows: [], _notFound: true };
    }
    throw e;
  }
}

/** Tab visibility: any of account / competitor / market-updates / persona with rows. Empty persona is not a hide. */
export function competitorModuleHasTabData({
  accountBarometer,
  personaBarometer,
  competitorView,
  marketUpdates,
} = {}) {
  return (
    (accountBarometer?.rows?.length || 0) > 0 ||
    (competitorView?.rows?.length || 0) > 0 ||
    (marketUpdates?.rows?.length || 0) > 0 ||
    (personaBarometer?.rows?.length || 0) > 0
  );
}

/** Session-cached gate: disabled | empty | ready */
export async function probeCompetitorModule(tenantId) {
  const tid = competitorTenantId(tenantId);
  if (!tid) return 'disabled';
  const cached = getCachedCompetitorModuleStatus(tid);
  // 'empty' must NOT be sticky: a tenant gains competitor rows after it is first
  // opened, and a cached 'empty' hides the tab for the life of the session.
  // 'disabled' is only sticky inside DISABLED_TTL_MS (the getter drops it after
  // that) so an admin can turn the module on without the user closing the tab.
  // 'ready' stays sticky; a later disable 404s inside the tab components.
  if (cached === 'disabled' || cached === 'ready') {
    return cached;
  }

  try {
    const [accountData, personaData, competitorView, marketUpdates] = await Promise.all([
      competitorFetchAllowMissing('/api/competitors/account-barometer', tid, {}),
      competitorFetchAllowMissing('/api/competitors/persona-barometer', tid, {}),
      competitorFetchAllowMissing('/api/competitors/competitor-view', tid, {}),
      competitorFetchAllowMissing('/api/competitors/market-updates', tid, {}),
    ]);
    const probes = [accountData, personaData, competitorView, marketUpdates];
    if (probes.every((d) => d?._notFound)) {
      setCachedCompetitorModuleStatus(tid, 'disabled');
      return 'disabled';
    }
    const status = competitorModuleHasTabData({
      accountBarometer: accountData,
      personaBarometer: personaData,
      competitorView,
      marketUpdates,
    })
      ? 'ready'
      : 'empty';
    setCachedCompetitorModuleStatus(tid, status);
    return status;
  } catch (e) {
    if (e instanceof CompetitorModuleDisabledError || e?.status === 404) {
      setCachedCompetitorModuleStatus(tid, 'disabled');
      return 'disabled';
    }
    throw e;
  }
}

export function fetchMarketSummary(tenantId, filters = {}) {
  return competitorFetch('/api/competitors/market-summary', tenantId, filters);
}

export function fetchCompetitorView(tenantId, filters = {}) {
  return competitorFetch('/api/competitors/competitor-view', tenantId, filters);
}

export function fetchAccountBarometer(tenantId, filters = {}) {
  return competitorFetch('/api/competitors/account-barometer', tenantId, filters);
}

export function fetchPersonaBarometer(tenantId, filters = {}) {
  return competitorFetch('/api/competitors/persona-barometer', tenantId, filters);
}

export function fetchMarketUpdates(tenantId, filters = {}) {
  return competitorFetch('/api/competitors/market-updates', tenantId, filters);
}

export function fetchCompetitorCountries(tenantId) {
  if (!tenantId) return Promise.resolve({ countries: [] });
  const cached = competitorCountriesCache.get(tenantId);
  if (cached) return Promise.resolve(cached);
  return competitorFetch('/api/competitors/countries', tenantId, {}).then((data) => {
    competitorCountriesCache.set(tenantId, data);
    return data;
  });
}

export function fetchAccountCompetitiveProximity(tenantId, accountId, filters = {}) {
  const id = encodeURIComponent(String(accountId).trim());
  return competitorFetch(`/api/accounts/${id}/competitive-proximity`, tenantId, filters);
}

export function fetchPersonaCompetitiveProximity(tenantId, personId, filters = {}) {
  const id = encodeURIComponent(String(personId).trim());
  return competitorFetch(`/api/personas/${id}/competitive-proximity`, tenantId, filters);
}

export async function fetchCompetitorEntitySignals(
  entityName,
  tenantId,
  where = {},
  orderBy = '',
  limit = 10000,
) {
  const tid = competitorTenantId(tenantId);
  const params = new URLSearchParams({
    where: JSON.stringify(where),
    tenant_id: tid,
    signal_focus: 'competitor',
  });
  if (orderBy) params.set('order', orderBy);
  if (limit) params.set('limit', String(limit));

  const res = await apiFetch(
    `/api/entities/${encodeURIComponent(entityName)}?${params.toString()}`,
    {
      credentials: 'include',
      headers: tid ? { 'X-Tenant-ID': tid } : undefined,
    },
  );
  if (!res.ok) {
    const err = new Error(`${entityName} competitor signals failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export async function fetchMarketUpdatesSafe(tenantId, filters = {}) {
  try {
    const data = await fetchMarketUpdates(tenantId, filters);
    return data?.rows || [];
  } catch (e) {
    if (e instanceof CompetitorModuleDisabledError || e?.status === 404) {
      return [];
    }
    throw e;
  }
}

export async function fetchAllCompetitorMarketData(tenantId, country = 'all') {
  const filters = countryFilterToParams(country);
  const [marketSummary, competitorView, accountBarometer, personaBarometer] =
    await Promise.all([
      fetchMarketSummary(tenantId, filters),
      fetchCompetitorView(tenantId, filters),
      fetchAccountBarometer(tenantId, filters),
      competitorFetchAllowMissing('/api/competitors/persona-barometer', tenantId, filters),
    ]);
  return { marketSummary, competitorView, accountBarometer, personaBarometer };
}
